/* ============================================================================
 * hourglass-timer / index.js   — orchestrator & mount
 *
 * window.HourglassTimer.mount(slug, config) builds the whole tool inside the
 * page's [data-hourglass-root]: the canvas hourglass, transport controls,
 * duration input, presets and option pickers. It owns the rAF loop, the
 * background-tab fallback, the wake lock, settings persistence, keyboard
 * shortcuts, the tab-title countdown and telemetry.
 *
 * config = { slug, i18n:{...}, presets:[{key,secs}], maxParticles }
 * ========================================================================== */

/* Sub-modules are pulled in dynamically with the SAME version query as this
 * entry (window.__HG_ASSET_V, set by the blade). A plain `import './x.js'` is
 * UNVERSIONED, so a stale Service-Worker copy of one module can pair with a
 * fresh copy of another — exactly the cross-module mismatch that surfaced as
 * `engine.setRange is not a function`. Version-matching the imports makes that
 * impossible. */
let createClock, createLayout, createSandEngine, createRenderer, createAudio, createOrientation;

/* ---- telemetry — platform-standard helpers (layouts/app.blade.php) ------ */
function makeTelemetry(slug) {
    const has = (n) => typeof window[n] === 'function';
    return {
        // funnel stage (idle → processing → complete) — drives the Pino dashboard
        stage: (s, meta) => { try { if (has('__stage')) window.__stage(s, meta || {}); } catch (e) {} },
        // discrete feature interactions
        feature: (f, extra) => {
            try {
                if (has('__aeFeature')) window.__aeFeature(f, extra || {});
                else if (has('__ae')) window.__ae('feature_use', Object.assign({ slug, f }, extra || {}));
            } catch (e) {}
        },
        // natural timer completion — also fires __stage('complete')
        done: (extra) => {
            try {
                if (has('__aeToolComplete')) window.__aeToolComplete(extra || {});
                else if (has('__ae')) window.__ae('tool_complete', Object.assign({ slug }, extra || {}));
            } catch (e) {}
        },
        error: (msg, ctx) => { try { if (has('__aeToolError')) window.__aeToolError({ slug, msg: String(msg), ctx }); } catch (e) {} },
    };
}

const perfNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}
function fmtTime(ms) {
    const tot = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(tot / 3600), m = Math.floor((tot % 3600) / 60), s = tot % 60;
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    return h > 0 ? h + ':' + pad(m) + ':' + pad(s) : pad(m) + ':' + pad(s);
}
const MAX_SEC = 99 * 3600 + 3599;

function mount(slug, config) {
    const root = document.querySelector('[data-hourglass-root]');
    if (!root) return { destroy() {} };

    const i18n = config.i18n || {};
    const T = (k) => i18n[k] || k;
    const tel = makeTelemetry(slug);
    const settings = (typeof window !== 'undefined' && window.__toolSettings) || null;
    const reduceMotion = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const origTitle = document.title;

    const load = (k, d) => { try { return settings ? settings.load(slug, k, d) : d; } catch (e) { return d; } };
    const save = (k, v) => { try { if (settings) settings.save(slug, k, v); } catch (e) {} };

    /* ---- initial duration: ?d=<seconds> / ?m=<minutes> deep-link wins --- */
    let urlDur = 0;
    try {
        const q = new URLSearchParams(location.search);
        if (q.has('d')) urlDur = parseInt(q.get('d'), 10) || 0;
        else if (q.has('m')) urlDur = Math.round((parseFloat(q.get('m')) || 0) * 60);
    } catch (e) {}

    /* ---- state ---------------------------------------------------------- */
    let durationSec = urlDur > 0
        ? Math.min(MAX_SEC, urlDur)
        : Math.max(1, parseInt(load('dur', 300), 10) || 300);
    let frame = load('frame', 'wood');
    let sandColor = load('sand', 'classic');
    let soundOn = load('sound', true) !== false;
    let loopOn = load('loop', false) === true;
    let deviceFlipOn = false;                       // always opt-in per session
    let loopCount = 0;
    let notifyAsked = false;
    // the current run drains the bottom fill fraction botStart→botEnd; a flip
    // rewrites these so only the sand actually in the bottom pours back.
    let runBotStart = 0, runBotEnd = 1;
    let pendingFlip = null;
    let flipCountDbg = 0;               // test hook only

    /* ---- engine + renderer --------------------------------------------- */
    const layout = createLayout({ grid: 120 });
    const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) < 720;
    const engine = createSandEngine(layout, { maxParticles: isMobile ? 90 : 140 });
    const clock = createClock({ totalMs: durationSec * 1000 });
    const audio = createAudio();
    let renderer = null;

    /* ---- DOM ------------------------------------------------------------ */
    root.innerHTML = '';
    root.className = 'hg-root';

    const stage = el('div', 'hg-stage');
    const canvas = el('canvas', 'hg-canvas');
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', T('aria_canvas'));
    const readout = el('div', 'hg-readout');
    readout.setAttribute('role', 'timer');
    readout.setAttribute('aria-live', 'off');
    readout.textContent = fmtTime(durationSec * 1000);
    stage.appendChild(canvas);

    // Two columns: the glass on the left, all the controls on the right. On a
    // narrow screen the grid collapses to a single stacked column (CSS). This
    // uses the wide card's horizontal space and keeps the page short.
    const leftCol = el('div', 'hg-left');
    const rightCol = el('div', 'hg-right');
    leftCol.appendChild(stage);
    leftCol.appendChild(readout);       // own row BELOW the glass — never overlaps the cap

    // transport
    const transport = el('div', 'hg-transport');
    const resetBtn = el('button', 'hg-btn hg-btn-ghost', T('reset'));
    const primaryBtn = el('button', 'hg-btn hg-btn-primary', T('start'));
    const flipBtn = el('button', 'hg-btn hg-btn-ghost', T('flip'));
    [resetBtn, primaryBtn, flipBtn].forEach((b) => { b.type = 'button'; });
    primaryBtn.setAttribute('aria-keyshortcuts', 'Space');
    resetBtn.setAttribute('aria-keyshortcuts', 'R');
    flipBtn.setAttribute('aria-keyshortcuts', 'F');
    rightCol.appendChild(transport);
    transport.appendChild(resetBtn);
    transport.appendChild(primaryBtn);
    transport.appendChild(flipBtn);

    // duration
    const durWrap = el('div', 'hg-duration');
    durWrap.appendChild(el('span', 'hg-field-label', T('duration_label')));
    const durInputs = el('div', 'hg-dur-inputs');
    const unitKeys = [['h', 'hours', 99], ['m', 'minutes', 59], ['s', 'seconds', 59]];
    const durFields = {};
    unitKeys.forEach(([u, lblKey, max]) => {
        const cell = el('div', 'hg-dur-cell');
        const minus = el('button', 'hg-spin', '−'); minus.type = 'button';
        minus.setAttribute('aria-label', '−');
        const inp = el('input', 'hg-dur-input');
        inp.type = 'text'; inp.inputMode = 'numeric'; inp.setAttribute('aria-label', T(lblKey));
        const plus = el('button', 'hg-spin', '+'); plus.type = 'button';
        plus.setAttribute('aria-label', '+');
        const unit = el('span', 'hg-dur-unit', T(u + '_unit'));
        cell.appendChild(minus); cell.appendChild(inp); cell.appendChild(plus); cell.appendChild(unit);
        durInputs.appendChild(cell);
        durFields[u] = { inp, max, minus, plus };
        // Press-and-HOLD to repeat, accelerating — telemetry showed users
        // rage-tapping these +/− steppers because each click moved only ±1
        // (setting 25 min meant ~20 taps). Native number-spinners auto-repeat;
        // these custom ones didn't, so we rebuild that here.
        holdRepeat(minus, () => bumpUnit(u, -1));
        holdRepeat(plus, () => bumpUnit(u, 1));
        inp.addEventListener('input', () => { inp.value = inp.value.replace(/[^0-9]/g, ''); });
        inp.addEventListener('change', commitDuration);
        inp.addEventListener('blur', commitDuration);
        // arrow keys in the field nudge the value (and Shift = ×5)
        inp.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); bumpUnit(u, e.shiftKey ? 5 : 1); commitConfigure('keyboard'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); bumpUnit(u, e.shiftKey ? -5 : -1); commitConfigure('keyboard'); }
        });
    });
    durWrap.appendChild(durInputs);
    rightCol.appendChild(durWrap);

    // presets
    const presetWrap = el('div', 'hg-presets');
    presetWrap.setAttribute('role', 'group');
    presetWrap.setAttribute('aria-label', T('presets_label'));
    const presets = config.presets || [];
    presets.forEach((pr) => {
        const chip = el('button', 'hg-chip', T(pr.key));
        chip.type = 'button';
        chip.dataset.secs = pr.secs;
        chip.addEventListener('click', () => { if (!isBusy()) applyDuration(pr.secs, 'preset'); });
        presetWrap.appendChild(chip);
    });
    rightCol.appendChild(presetWrap);

    // options
    const opts = el('div', 'hg-options');
    function pickerSelect(labelKey, optionKeys, current, onChange) {
        const w = el('label', 'hg-opt');
        w.appendChild(el('span', 'hg-opt-label', T(labelKey)));
        const sel = el('select', 'hg-select');
        optionKeys.forEach((k) => {
            const o = el('option', null, T(k.tk));
            o.value = k.v;
            if (k.v === current) o.selected = true;
            sel.appendChild(o);
        });
        sel.addEventListener('change', () => onChange(sel.value));
        w.appendChild(sel);
        return w;
    }
    opts.appendChild(pickerSelect('frame_label', [
        { v: 'wood', tk: 'frame_wood' }, { v: 'brass', tk: 'frame_brass' }, { v: 'crystal', tk: 'frame_crystal' },
    ], frame, (v) => { frame = v; save('frame', v); if (renderer) renderer.setTheme(v); tel.feature('frame', { v }); }));
    opts.appendChild(pickerSelect('sand_label', [
        { v: 'classic', tk: 'sand_classic' }, { v: 'rose', tk: 'sand_rose' },
        { v: 'graphite', tk: 'sand_graphite' }, { v: 'azure', tk: 'sand_azure' }, { v: 'emerald', tk: 'sand_emerald' },
    ], sandColor, (v) => { sandColor = v; save('sand', v); if (renderer) renderer.setSand(v); tel.feature('sand', { v }); }));

    // a toggle button; returns { el, set(bool) } so callers can re-sync state
    function toggleBtn(labelOn, labelOff, on, onChange) {
        const b = el('button', 'hg-toggle');
        b.type = 'button';
        const paint = () => {
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
            b.textContent = on ? labelOn : labelOff;
        };
        paint();
        b.addEventListener('click', () => { on = !on; paint(); onChange(on); });
        return { el: b, set(v) { on = !!v; paint(); } };
    }
    const soundTog = toggleBtn(T('sound_on'), T('sound_off'), soundOn, (on) => {
        soundOn = on; save('sound', on); audio.setMuted(!on); tel.feature('sound', { on });
    });
    const loopTog = toggleBtn(T('loop_on'), T('loop_off'), loopOn, (on) => {
        loopOn = on; save('loop', on); tel.feature('loop', { on });
    });
    opts.appendChild(soundTog.el);
    opts.appendChild(loopTog.el);

    let orient = null;
    if (typeof window !== 'undefined' && 'DeviceOrientationEvent' in window) {
        orient = createOrientation(() => { if (!flipping) doFlip(); });
        const devCtl = toggleBtn(T('device_flip'), T('device_flip'), false, (on) => {
            if (on) {
                orient.enable().then((ok) => {
                    deviceFlipOn = ok;
                    tel.feature('device_flip', { on: true, granted: ok });
                    if (!ok) { devCtl.set(false); announce(T('device_flip_denied')); }
                });
            } else {
                deviceFlipOn = false; orient.disable(); tel.feature('device_flip', { on: false });
            }
        });
        opts.appendChild(devCtl.el);
    }

    const fsBtn = el('button', 'hg-toggle', T('fullscreen'));
    fsBtn.type = 'button';
    fsBtn.addEventListener('click', toggleFullscreen);
    opts.appendChild(fsBtn);
    rightCol.appendChild(opts);

    // assemble the two columns
    root.appendChild(leftCol);
    root.appendChild(rightCol);

    // keyboard-shortcut hint (screen-reader + title)
    root.setAttribute('aria-keyshortcuts', 'Space R F');

    const aria = el('div', 'hg-aria');
    aria.setAttribute('aria-live', 'polite');
    root.appendChild(aria);
    function announce(msg) {
        aria.textContent = '';
        setTimeout(() => { aria.textContent = msg; }, 40);
        try { if (typeof window.__announce === 'function') window.__announce(msg); } catch (e) {}
    }

    /* ---- duration helpers ---------------------------------------------- */
    function syncDurationInputs() {
        durFields.h.inp.value = Math.floor(durationSec / 3600);
        durFields.m.inp.value = Math.floor((durationSec % 3600) / 60);
        durFields.s.inp.value = durationSec % 60;
    }
    function readDurationInputs() {
        const h = Math.min(99, parseInt(durFields.h.inp.value, 10) || 0);
        const m = Math.min(59, parseInt(durFields.m.inp.value, 10) || 0);
        const s = Math.min(59, parseInt(durFields.s.inp.value, 10) || 0);
        return h * 3600 + m * 60 + s;
    }
    // Update the duration WITHOUT persisting or firing telemetry — used on every
    // step of a held repeat so a 2-second hold doesn't emit 25 `configure`
    // events or hammer localStorage. The visible readout still tracks live.
    let cfgDirty = false, cfgVia = 'input';
    function setDurationLive(sec) {
        durationSec = Math.max(1, Math.min(MAX_SEC, Math.round(sec)));
        syncDurationInputs();
        clock.setDuration(durationSec * 1000);
        updateReadout(true);            // the readout is DOM — sand needn't redraw
        highlightPreset();
        cfgDirty = true;
    }
    // Persist + emit a single `configure` once the interaction settles. The sand
    // (full top when idle) only needs the one re-snap here, not on every held
    // step — snap() runs a full relaxation sweep and is far too heavy for 25Hz.
    function commitConfigure(via) {
        if (!cfgDirty) return;
        cfgDirty = false;
        save('dur', durationSec);
        if (!isBusy()) { resetGlassFull(); engine.snap(0); }   // a re-dialled glass is full again
        wake();
        tel.feature('configure', { sec: durationSec, via: via || cfgVia });
    }
    function applyDuration(sec, via) {
        setDurationLive(sec);
        cfgVia = via || 'input';
        commitConfigure(cfgVia);
    }
    function commitDuration() {
        if (isBusy()) { syncDurationInputs(); return; }
        applyDuration(readDurationInputs() || durationSec, 'input');
    }
    function bumpUnit(u, dir) {
        if (isBusy()) return;
        const f = durFields[u];
        let v = (parseInt(f.inp.value, 10) || 0) + dir;
        v = Math.max(0, Math.min(f.max, v));
        f.inp.value = v;
        cfgVia = 'stepper';
        setDurationLive(readDurationInputs() || durationSec);
    }
    /* Press-and-hold a stepper button → one step, a beat, then an accelerating
       repeat (≈300ms → 40ms). The press belongs to the shared window.__holdRepeat
       (timbrica-core.js): it swallows the click of the same press. Until 29.09.2026
       the guard here was reset by setTimeout(0) on release, and a finger tap's click
       — a later task — often came after it: one tap, two steps. The click handler
       below is now only the keyboard / assistive-tech path: one clean step. */
    function holdRepeat(btn, step) {
        if (window.__holdRepeat) {
            window.__holdRepeat(btn, () => { step(); }, {
                delay: 300,
                period: (n) => Math.max(40, 300 * Math.pow(0.8, n)),
                when: () => !isBusy(),
                onEnd: () => commitConfigure('stepper'),
            });
        }
        btn.addEventListener('click', () => { step(); commitConfigure('stepper'); });
    }
    function highlightPreset() {
        presetWrap.querySelectorAll('.hg-chip').forEach((c) => {
            c.classList.toggle('is-active', parseInt(c.dataset.secs, 10) === durationSec);
        });
    }

    /* ---- transport ------------------------------------------------------ */
    function isBusy() { return clock.state === 'running' || flipping; }

    function refreshControls() {
        const st = clock.state;
        primaryBtn.textContent = st === 'running' ? T('pause')
            : st === 'paused' ? T('resume') : T('start');
        primaryBtn.classList.toggle('is-running', st === 'running');
        const lock = (st === 'running');
        durInputs.classList.toggle('is-locked', lock);
        Object.values(durFields).forEach((f) => {
            f.inp.disabled = lock; f.minus.disabled = lock; f.plus.disabled = lock;
        });
        presetWrap.querySelectorAll('.hg-chip').forEach((c) => { c.disabled = lock; });
        resetBtn.disabled = (st === 'idle');
    }

    function maybeAskNotify() {
        if (notifyAsked || typeof Notification === 'undefined') return;
        notifyAsked = true;
        try { if (Notification.permission === 'default') Notification.requestPermission(); } catch (e) {}
    }

    // a fresh full glass: all the sand in the top, draining 0→1
    function resetGlassFull() {
        runBotStart = 0; runBotEnd = 1;
        engine.setRange(0, 1);
    }
    // how full the bottom bulb is RIGHT NOW (0 = empty, 1 = all the sand) — this
    // is the amount a flip raises back to the top.
    function currentBottomFraction() {
        return runBotStart + clock.progress() * (runBotEnd - runBotStart);
    }

    function doStart() {
        audio.unlock();
        maybeAskNotify();
        resetGlassFull();
        clock.reset();
        clock.setDuration(durationSec * 1000);
        engine.snap(0);
        loopCount = 0;
        engine.setPaused(false);
        clock.start();
        requestWakeLock();
        wake();
        announce(T('started'));
        tel.stage('processing', { sec: durationSec });
        tel.feature('start', { sec: durationSec });
        refreshControls();
    }
    function doPause() {
        clock.pause(); engine.setPaused(true); audio.setFlow(0);
        announce(T('paused')); releaseWakeLock(); wake();
        tel.feature('pause'); refreshControls();
    }
    function doResume() {
        audio.unlock(); engine.setPaused(false); clock.resume();
        requestWakeLock(); wake(); announce(T('resumed'));
        tel.feature('resume'); refreshControls();
    }
    function doReset() {
        clock.reset(); engine.setPaused(false); resetGlassFull(); engine.snap(0); audio.setFlow(0);
        releaseWakeLock(); loopCount = 0;
        updateReadout(true); wake();
        announce(T('reset_done'));
        tel.stage('idle'); tel.feature('reset');
        refreshControls();
    }

    let flipping = false, flipStart = 0;
    const FLIP_MS = 620;
    /* A real-hourglass flip: only the sand that's ALREADY in the bottom is
       raised back up and pours again — so flipping a 5-minute glass 30s in gives
       a 30-second run, not a fresh 5 minutes. A full / barely-started glass has
       nothing in the bottom, so there we restart the whole glass. */
    function doFlip() {
        if (flipping) return;
        audio.unlock();
        flipCountDbg++;
        const f = currentBottomFraction();
        const partialMs = Math.round(f * durationSec * 1000);   // time the raised sand takes to pour
        // A fresh glass, or one flipped within the first instant, has nothing
        // meaningful in the bottom → restart the whole glass. Otherwise honour
        // the real hourglass and pour back exactly the elapsed amount.
        const full = clock.state === 'idle' || partialMs < 700;
        pendingFlip = full
            ? { botStart: 0, botEnd: 1, durMs: durationSec * 1000 }
            : { botStart: 1 - f, botEnd: 1, durMs: partialMs };
        tel.feature('flip', { loop: loopOn, mode: full ? 'full' : 'partial', f: Math.round(f * 100) });
        if (reduceMotion) { applyFlip(); announce(T('flipped')); return; }
        flipping = true;
        flipStart = perfNow();
        if (full) engine.snap(1);           // full path: π shows full-top (seamless)
        engine.setPaused(true);             // partial path: the CURRENT amounts rotate
        wake();
        announce(T('flipped'));
        refreshControls();
    }
    // commit the post-flip state: swap the bulbs, re-arm the clock for the new
    // (shorter) run, and let the sand settle as it starts pouring.
    function applyFlip() {
        runBotStart = pendingFlip.botStart;
        runBotEnd = pendingFlip.botEnd;
        engine.setRange(runBotStart, runBotEnd);
        engine.setPaused(false);
        engine.snap(0);                     // settled new start (top = the raised sand)
        clock.reset();
        clock.setDuration(pendingFlip.durMs);
        clock.start();
        requestWakeLock();
        wake();
        refreshControls();
    }
    function finishFlip() {
        flipping = false;
        applyFlip();
    }

    primaryBtn.addEventListener('click', () => {
        const st = clock.state;
        if (st === 'running') doPause();
        else if (st === 'paused') doResume();
        else doStart();
    });
    resetBtn.addEventListener('click', doReset);
    flipBtn.addEventListener('click', doFlip);

    /* ---- clock completion ---------------------------------------------- */
    clock.onDone = () => {
        engine.snap(1);
        if (soundOn) audio.chime();
        audio.setFlow(0);
        announce(T('time_up'));
        try { if (typeof window.__notify === 'function') window.__notify(T('time_up'), { tag: 'hourglass-' + slug }); } catch (e) {}
        if (loopOn) {
            loopCount++;
            tel.feature('loop_cycle', { n: loopCount });
            setTimeout(() => { if (loopOn && !flipping) doFlip(); }, 900);
        } else {
            tel.done({ action: 'timer_complete', sec: durationSec, looped: loopCount });
            if (renderer) renderer.flash();   // completion flourish
            releaseWakeLock();
            wake();                           // keep rendering the last grains + the flash
            refreshControls();
        }
    };

    /* ---- wake lock ------------------------------------------------------ */
    let wakeLock = null;
    function requestWakeLock() {
        try {
            if (navigator.wakeLock && !wakeLock) {
                navigator.wakeLock.request('screen').then((wl) => { wakeLock = wl; }).catch(() => {});
            }
        } catch (e) {}
    }
    function releaseWakeLock() {
        try { if (wakeLock) { wakeLock.release(); wakeLock = null; } } catch (e) {}
    }

    /* ---- fullscreen / ambient mode -------------------------------------
       The CSS `.is-fullscreen` class is the source of truth — it works
       everywhere, including iOS Safari, which has no Fullscreen API on
       non-video elements. The real API is attempted as a bonus on desktop. */
    function applyFs(on) {
        root.classList.toggle('is-fullscreen', on);
        fsBtn.textContent = on ? T('exit_fullscreen') : T('fullscreen');
        fsBtn.classList.toggle('is-on', on);
        wake();
        resize();
    }
    function toggleFullscreen() {
        const on = !root.classList.contains('is-fullscreen');
        applyFs(on);
        try {
            if (on && root.requestFullscreen) {
                const p = root.requestFullscreen();
                if (p && p.catch) p.catch(() => {});
            } else if (!on && document.fullscreenElement && document.exitFullscreen) {
                document.exitFullscreen();
            }
        } catch (e) {}
        tel.feature('fullscreen', { on });
    }
    document.addEventListener('fullscreenchange', () => {
        // browser left native fullscreen (Esc) — drop the CSS class to match
        if (!document.fullscreenElement && root.classList.contains('is-fullscreen')) applyFs(false);
    });

    /* ---- keyboard shortcuts -------------------------------------------- */
    function onKey(e) {
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
        const k = e.key;
        const kl = window.__keyLetter ? window.__keyLetter(e) : String(k || '').toLowerCase();
        if (k === ' ' || k === 'Spacebar' || k === 'Space') { e.preventDefault(); primaryBtn.click(); }
        else if (kl === 'r') { e.preventDefault(); if (!resetBtn.disabled) doReset(); }
        else if (kl === 'f') { e.preventDefault(); doFlip(); }
        else if (k === 'Escape' && root.classList.contains('is-fullscreen')) { applyFs(false); }
        else return;
        wake();
        tel.feature('hotkey', { key: (k === ' ' ? 'space' : (kl || k.toLowerCase())) });
    }
    document.addEventListener('keydown', onKey);

    /* ---- sizing --------------------------------------------------------- */
    function resize() {
        const rect = stage.getBoundingClientRect();
        if (rect.width < 8 || rect.height < 8) return;
        if (renderer) renderer.setSize(rect.width, rect.height);
        wake();
    }
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(() => resize()).observe(stage);
    } else if (typeof window !== 'undefined') {
        window.addEventListener('resize', resize);
    }

    /* ---- readout + tab-title countdown --------------------------------- */
    let lastShown = '';
    function updateReadout(force) {
        const txt = fmtTime(clock.getRemaining());
        if (force || txt !== lastShown) {
            lastShown = txt;
            readout.textContent = txt;
            // live tab-title countdown so a backgrounded timer is visible
            if (clock.state === 'running') document.title = '⏳ ' + txt;
            else if (clock.state === 'done') document.title = '✅ ' + origTitle;
            else document.title = origTitle;
        }
        readout.classList.toggle('is-running', clock.state === 'running');
        readout.classList.toggle('is-done', clock.state === 'done');
    }

    /* ---- main loop — sleeps when nothing is animating (battery) -------- */
    let rafId = 0, lastTs = 0, wakeUntil = 0;
    function wake() {
        wakeUntil = perfNow() + 1800;
        if (!rafId && typeof document !== 'undefined' && !document.hidden) {
            lastTs = 0;
            rafId = requestAnimationFrame(renderLoop);
        }
    }
    function renderLoop(ts) {
        rafId = requestAnimationFrame(renderLoop);
        let dt = lastTs ? (ts - lastTs) / 1000 : 1 / 60;
        lastTs = ts;
        if (dt > 0.1) dt = 0.1;

        if (flipping) {
            const k = Math.min(1, (ts - flipStart) / FLIP_MS);
            const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
            renderer && renderer.render(engine.state(), { flipAngle: eased * Math.PI, dt });
            if (k >= 1) finishFlip();
            return;
        }

        clock.tick();
        const running = clock.state === 'running';
        const active = running || perfNow() < wakeUntil;
        const p = clock.progress();

        if (active) {
            if (!reduceMotion) engine.step(p, dt);
            else engine.snap(p);
        }
        const est = engine.state();
        renderer && renderer.render(est, { dt, pouring: running });
        audio.setFlow(running && soundOn ? est.flow * 90 : 0);
        updateReadout(false);

        if (!active) { cancelAnimationFrame(rafId); rafId = 0; }   // settle → sleep
    }

    /* ---- background-tab fallback --------------------------------------- */
    let bgTimer = 0;
    function onVisibility() {
        if (document.hidden) {
            if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
            if (!bgTimer) bgTimer = setInterval(() => { clock.tick(); }, 250);
        } else {
            if (bgTimer) { clearInterval(bgTimer); bgTimer = 0; }
            engine.snap(clock.progress());          // catch up after hidden time
            if (clock.state === 'running') requestWakeLock();
            wake();
        }
    }
    document.addEventListener('visibilitychange', onVisibility);

    // any pointer interaction inside the tool wakes the render loop
    root.addEventListener('pointerdown', wake, true);

    // tap the hourglass itself to start / pause — the biggest, most obvious
    // target on the page, what people instinctively reach for.
    canvas.style.cursor = 'pointer';
    canvas.addEventListener('click', () => {
        if (flipping) return;
        primaryBtn.click();
        tel.feature('canvas_tap', { st: clock.state });
    });

    /* ---- boot ----------------------------------------------------------- */
    renderer = createRenderer(canvas, layout, { theme: frame, sand: sandColor, reduceMotion });
    audio.setMuted(!soundOn);
    syncDurationInputs();
    highlightPreset();
    refreshControls();
    resize();
    engine.snap(0);
    updateReadout(true);
    wake();
    tel.stage('idle');
    tel.feature('ready', { url_dur: urlDur > 0 ? 1 : 0 });

    /* ---- test hook ------------------------------------------------------ */
    window.__hgDebug = {
        slug, clock, engine,
        setDuration: (sec) => applyDuration(sec, 'debug'),
        start: doStart, pause: doPause, resume: doResume, reset: doReset, flip: doFlip,
        state: () => clock.state,
        remaining: () => clock.getRemaining(),
        progress: () => clock.progress(),
        flips: () => flipCountDbg,
        bottomFraction: () => currentBottomFraction(),
        particleCount: () => engine.state().particles.length,
        engineDebug: () => engine.debug(),
        isFlipping: () => flipping,
        fireOrientation: (beta) => { if (orient) orient._fire(beta); },
        setLoop: (v) => { loopOn = !!v; loopTog.set(loopOn); },
        toggleFullscreen,
        isAwake: () => !!rafId,
    };
    window.__hgFireOrientation = (beta) => { if (orient) orient._fire(beta); };

    return {
        destroy() {
            if (rafId) cancelAnimationFrame(rafId);
            if (bgTimer) clearInterval(bgTimer);
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('visibilitychange', onVisibility);
            releaseWakeLock();
            audio.suspend();
            if (orient) orient.disable();
            document.title = origTitle;
            root.innerHTML = '';
        },
    };
}

/* ---- load the version-matched sub-modules, then expose + drain the queue -- */
(function boot() {
    const V = (typeof window !== 'undefined' && window.__HG_ASSET_V) ? ('?v=' + window.__HG_ASSET_V) : '';
    Promise.all([
        import('./clock.js' + V),
        import('./sand-engine.js' + V),
        import('./renderer.js' + V),
        import('./audio.js' + V),
        import('./orientation.js' + V),
    ]).then(function (mods) {
        createClock = mods[0].createClock;
        createLayout = mods[1].createLayout;
        createSandEngine = mods[1].createSandEngine;
        createRenderer = mods[2].createRenderer;
        createAudio = mods[3].createAudio;
        createOrientation = mods[4].createOrientation;
        window.HourglassTimer = { mount };
        const q = window.__HG_QUEUE;
        if (q && q.length) q.splice(0).forEach((args) => { try { mount(args[0], args[1]); } catch (e) {} });
        window.dispatchEvent(new Event('hourglass-timer:ready'));
    }).catch(function (e) { try { console.error('hourglass-timer: module load failed', e); } catch (_) {} });
})();

export default { mount };
